import React, { forwardRef } from 'react';

import { cn } from '../../utils';
import { ariaUtils } from '../../utils/accessibility';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
  dot?: boolean;
  // Accessibility props
  'aria-label'?: string;
  'aria-describedby'?: string;
  semanticMeaning?: 'status' | 'count' | 'category' | 'notification' | 'alert';
  screenReaderText?: string;
  announceChanges?: boolean;
}

const variants = {
  default: 'bg-gray-100 text-gray-800 border-gray-200',
  success: 'bg-green-100 text-green-800 border-green-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  danger: 'bg-red-100 text-red-800 border-red-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  outline: 'bg-white text-gray-700 border-gray-300',
};

const sizes = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      children,
      variant = 'default',
      size = 'md',
      rounded = true,
      dot = false,
      className,
      semanticMeaning = 'status',
      screenReaderText,
      announceChanges = false,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Generate unique ID for accessibility
    const badgeId = ariaUtils.generateId('badge');

    // Determine appropriate role based on semantic meaning
    const getRole = () => {
      switch (semanticMeaning) {
        case 'alert':
          return 'alert';
        case 'status':
          return 'status';
        case 'notification':
          return 'alert';
        case 'count':
          return 'img'; // For count badges that are decorative
        case 'category':
          return 'img'; // For category labels that are decorative
        default:
          return 'img';
      }
    };

    // Get appropriate aria-live setting
    const getAriaLive = () => {
      if (!announceChanges) return undefined;

      switch (semanticMeaning) {
        case 'alert':
        case 'notification':
          return 'assertive';
        case 'status':
        case 'count':
          return 'polite';
        default:
          return 'polite';
      }
    };

    // Generate screen reader text
    const getScreenReaderText = () => {
      if (screenReaderText) return screenReaderText;

      const content = typeof children === 'string' ? children : '';

      switch (semanticMeaning) {
        case 'status':
          return `Status: ${content}`;
        case 'count':
          return `Count: ${content}`;
        case 'category':
          return `Category: ${content}`;
        case 'notification':
          return `Notification: ${content}`;
        case 'alert':
          return `Alert: ${content}`;
        default:
          return content;
      }
    };

    const baseClasses = cn(
      // Base styles
      'inline-flex items-center justify-center font-medium border',
      'transition-colors duration-200',

      // Size
      !dot && sizes[size],

      // Dot specific styles
      dot && 'w-2 h-2 p-0',

      // Variant
      variants[variant],

      // Shape
      rounded && (dot ? 'rounded-full' : 'rounded-full'),
      !rounded && 'rounded-md',

      className
    );

    // For dot badges, don't render text content
    if (dot) {
      return (
        <span
          ref={ref}
          id={badgeId}
          className={baseClasses}
          role={getRole()}
          aria-label={ariaLabel || getScreenReaderText()}
          aria-describedby={ariaDescribedBy}
          aria-live={getAriaLive()}
          {...props}
        >
          {/* Screen reader text for dot badges */}
          <span className="sr-only">{getScreenReaderText()}</span>
        </span>
      );
    }

    return (
      <span
        ref={ref}
        id={badgeId}
        className={baseClasses}
        role={getRole()}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-live={getAriaLive()}
        {...props}
      >
        {children}

        {/* Additional screen reader context if needed */}
        {screenReaderText && screenReaderText !== children && (
          <span className="sr-only">{screenReaderText}</span>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
